import type { ConditionResource } from "../../condition";
import type { UnitResource } from "../../unit";
import type { VariantComponentResource } from "../../variant-component";

import {
  type PlaceOfSupplyRuleEnum,
  type ProductResource,
  type ProductStockFlagEnum,
  type SupplyTypeEnum,
} from "../../product";
import type { NoteResource } from "../../note";
import type { MarketplaceDataResource } from "../../marketplace";
import type { AttributeValueResource } from "../../attribute-value";
import type { PriceListItemResource } from "../../price-list-item";
import type { ProcessingTimeResource } from "../../processing-time";
import type { WarehousePlaceResource } from "../../warehouse-place";

import type { MediaAttributes, Nullable } from "../../../common.ts";
import type { VatResource } from "../../vat";

export interface VariantResource {
  id: string;
  gid: string;
  sku: string;
  title: string | null;
  condition?: ConditionResource;
  condition_id_inherited: boolean;
  size_inherited: boolean;
  is_short_description_printed_in_documents_inherited: boolean;
  has_best_before_date_inherited: boolean;
  is_intangible_product_inherited: boolean;
  supply_type_inherited: boolean;
  place_of_supply_rule_inherited: boolean;
  is_electron_return_option_inherited: boolean;
  is_customizable_product_inherited: boolean;
  amount_of_unit_per_package: string | null;
  amount_of_unit_per_package_inherited: boolean;
  volume_inherited: boolean;
  product_id: string;
  size_unit_id: string | null;
  unit_id: string | null;
  volume_unit_id: string | null;
  unit_inherited: boolean;
  status_inherited: boolean;
  weight_inherited: boolean;
  stock_flag_inherited: boolean;
  processing_time_id_inherited: boolean;
  title_inherited: boolean;
  dropshiper_stock: string;
  available_from: string;
  weight_unit_id: string | null;
  processing_time_id: string | null;
  short_description_inherited: boolean;
  long_description_inherited: boolean;
  condition_id: string | null;
  ean: string | null;
  price: string | null;
  price_inherited: boolean;
  short_description: string | null;
  long_description: string | null;
  // in_stock_information: string;
  // out_of_stock_information: string;
  status: VariantStatusEnum;
  stock: string | null;
  stock_external: string | null;
  stock_flag: ProductStockFlagEnum | null;
  processing_time?: ProcessingTimeResource;
  reserve_stock: string | null;
  is_short_description_printed_in_documents: boolean | null;
  has_best_before_date: boolean | null;
  is_intangible_product: boolean | null;
  supply_type: SupplyTypeEnum | null;
  place_of_supply_rule: PlaceOfSupplyRuleEnum | null;
  is_electron_return_option: boolean | null;
  is_customizable_product: boolean | null;
  is_product_set: boolean;
  weight: string | null;
  weight_unit?: UnitResource;
  length: string | null;
  width: string | null;
  height: string | null;
  sizeUnit?: UnitResource | null;
  volumeUnit?: UnitResource | null;

  volume: string | null;
  unit_quantity: string | null;
  unit?: UnitResource;
  recommended_retail_price: string | null;
  recommended_retail_price_inherited: boolean;
  components?: VariantComponentResource[];
  componentsCount?: number;
  componentsExists?: string;
  attributeValues?: AttributeValueResource[];
  definitionAttributeValues?: AttributeValueResource[];
  product?: ProductResource;
  priceListItems?: PriceListItemResource[];
  configNumbers?: Record<string, { value: string }>;
  notes?: NoteResource[];
  marketplaceData?: MarketplaceDataResource[];
  vats?: VatResource[];
  vat_ids?: string[];
  warehousePlaces?: WarehousePlaceResource[];

  marketplace_titles?: {
    [marketplaceId: string]: {
      value: string;
      is_inherited: boolean;
    };
  };

  marketplace_short_descriptions?: {
    [marketplaceId: string]: {
      value: string;
      is_inherited: boolean;
    };
  };

  marketplace_descriptions?: {
    [marketplaceId: string]: {
      value: string;
      is_inherited: boolean;
    };
  };

  images: { [uuid: string]: MediaAttributes };
}

export enum VariantStatusEnum {
  Active = "active",
  Inactive = "inactive",
  Blocked = "blocked",
}

export interface VariantStoreRequest {
  product_id: string;
  title: string;
  sku: string;
  condition_id?: Nullable<string>;
  ean?: Nullable<string>;
  price?: Nullable<string | number>;
  short_description?: Nullable<string>;
  long_description?: Nullable<string>;

  status?: Nullable<VariantStatusEnum>;
  stock?: Nullable<number>;
  stock_external?: Nullable<number>;
  stock_flag?: Nullable<ProductStockFlagEnum>;
  processing_time_id?: Nullable<string>;
  reserve_stock?: Nullable<number | string>;
  is_short_description_printed_in_documents?: Nullable<boolean>;
  has_best_before_date?: Nullable<boolean>;
  is_intangible_product?: Nullable<boolean>;
  supply_type?: Nullable<SupplyTypeEnum>;
  place_of_supply_rule?: Nullable<PlaceOfSupplyRuleEnum>;
  is_searchable?: Nullable<boolean>;
  is_electron_return_option?: Nullable<boolean>;
  is_customizable_product?: Nullable<boolean>;
  is_product_set?: Nullable<boolean>;
  weight?: Nullable<number | string>;
  weight_unit_id?: Nullable<string>;
  length?: Nullable<number | string>;
  width?: Nullable<number | string>;
  height?: Nullable<number | string>;
  size_unit_id?: Nullable<string>;
  volume?: Nullable<number | string>;
  volume_unit_id?: Nullable<string>;
  unit_quantity?: Nullable<number | string>;
  unit_id?: Nullable<string>;
  recommended_retail_price?: Nullable<number | string>;
  vats?: Nullable<string[] | { [countryId: string]: string }>;
  blockedMarketplaces?: Nullable<string[]>;

  long_description_inherited?: Nullable<boolean>;
  short_description_inherited?: Nullable<boolean>;

  configNumbers?: Nullable<{
    [id: string]: {
      value: string;
    };
  }>;

  marketplaceData?: Nullable<{
    [id: string]: {
      title: string | null;
      short_description: string | null;
      description: string | null;
    };
  }>;

  attributeValues?: Nullable<
    {
      id: string | null;
      attribute_id: string;
      title?: string | null;
    }[]
  >;
  images?: Nullable<{ [uuid: string]: MediaAttributes }>;
}

export interface VariantUpdateRequest {
  title?: Nullable<string>;

  warehousePlaces?: Nullable<string[]>;
  sku?: Nullable<string>;
  condition_id?: Nullable<string>;
  ean?: Nullable<string>;
  price?: Nullable<number | string>;
  short_description?: Nullable<string>;
  long_description?: Nullable<string>;
  status?: Nullable<VariantStatusEnum>;
  stock?: Nullable<number>;
  stock_external?: Nullable<string>;
  stock_flag?: Nullable<ProductStockFlagEnum>;
  processing_time_id?: Nullable<string>;
  reserve_stock?: Nullable<string>;
  is_short_description_printed_in_documents?: Nullable<boolean>;
  has_best_before_date?: Nullable<boolean>;
  is_intangible_product?: Nullable<boolean>;
  supply_type?: Nullable<SupplyTypeEnum>;
  place_of_supply_rule?: Nullable<PlaceOfSupplyRuleEnum>;
  is_searchable?: Nullable<boolean>;
  is_electron_return_option?: Nullable<boolean>;
  is_customizable_product?: Nullable<boolean>;
  is_product_set?: Nullable<boolean>;
  weight?: Nullable<number | string>;
  weight_unit_id?: Nullable<string>;
  length?: Nullable<number | string>;
  width?: Nullable<number | string>;
  height?: Nullable<number | string>;
  size_unit_id?: Nullable<string>;
  volume?: Nullable<number | string>;
  volume_unit_id?: Nullable<string>;
  unit_quantity?: Nullable<number | string>;
  unit_id?: Nullable<string>;
  recommended_retail_price?: Nullable<number | string>;
  vats?: Nullable<string[] | { [countryId: string]: string }>;
  blockedMarketplaces?: Nullable<string[]>;
  long_description_inherited?: Nullable<boolean>;
  short_description_inherited?: Nullable<boolean>;

  configNumbers?: Nullable<{
    [id: string]: {
      value: string;
    };
  }>;

  marketplaceData?: Nullable<{
    [id: string]: {
      title: string | null;
      short_description: string | null;
      description: string | null;
    };
  }>;

  //attributeValues?: VariantAttributeStoreRequest[] | null;

  definitionAttributeValues?: Nullable<
    {
      id: string | null;
      attribute_id: string;
      title?: string | null;
    }[]
  >;
  images?: Nullable<{ [uuid: string]: MediaAttributes }>;

  textFieldAttributeValues?:
    | {
        id: string | null;
        attribute_id: string;
        title: string | null;
      }[]
    | null;

  floatAttributeValues?:
    | {
        id: string | null;
        attribute_id: string;
        title: string | null;
      }[]
    | null;

  integerAttributeValues?:
    | {
        id: string | null;
        attribute_id: string;
        title: string | null;
      }[]
    | null;

  dateAttributeValues?:
    | {
        id: string | null;
        attribute_id: string;
        title: string | null;
      }[]
    | null;

  dateTimeAttributeValues?:
    | {
        id: string | null;
        attribute_id: string;
        title: string | null;
      }[]
    | null;

  selectionListAttributeValues?:
    | {
        id: string;
        attribute_id: string;
      }[]
    | null;
}
